import React, {useState, useEffect, useRef, useCallback} from 'react'
import Text from '../../../components/text/Text'
import WizSelectButton from '../../../components/button/WizSelectButton'
import UnderLineInput from '../../../components/input/UnderLineInput'
import WizButton from '../../../components/button/WizButton'
import {IPromotionItem, createPromotionItem, updatePromotionItem, SlotType} from '../../../apis/promotion/item'
import {postUploadImage} from '../../../apis/image/postUploadImage'
import WizImage from '../../../components/image/WizImage'
import {useImage} from '../../../utils/image'

// Slot별 권장 사이즈 정의
const SLOT_SIZE_CONFIG: Record<string, {pc?: {width: number; height: number}; mo?: {width: number; height: number}}> = {
    'leaderboard-banner': {
        pc: {width: 1554, height: 600},
        mo: {width: 960, height: 300},
    },
    'tradermap-card': {
        pc: {width: 1800, height: 450},
        mo: {width: 990, height: 300},
    },
    'landing-event': {
        pc: {width: 2560, height: 400},
        mo: {width: 640, height: 200},
    },
    'popup-event': {
        pc: {width: 800, height: 700},
        mo: {width: 800, height: 700},
    },
    'announce-event': {
        pc: {width: 960, height: 450},
        mo: {width: 960, height: 450},
    },
    'bottom-sheet': {
        pc: {width: 1350, height: 810},
        mo: {width: 1350, height: 810},
    },
    'account-banner': {
        pc: {width: 960, height: 240},
        mo: {width: 960, height: 240},
    },
    'leaderboard-header': {
        pc: {width: 960, height: 240},
        mo: {width: 960, height: 240},
    },
}

interface IProps {
    promotionId: string
    item?: IPromotionItem
    onSuccess: () => void
    onCancel: () => void
    onDelete?: () => void
}

interface SizeCheckResult {
    match: boolean
    actual: {width: number; height: number}
    expected: {width: number; height: number}
}

const slotOptions: {id: SlotType; value: string}[] = [
    {id: 'leaderboard-banner', value: 'LeaderboardTop'},
    {id: 'tradermap-card', value: 'traderMapTop'},
    {id: 'landing-event', value: 'LandingBanner'},
    {id: 'popup-event', value: 'LandingPopup'},
    {id: 'announce-event', value: 'AnnounceEvent'},
    {id: 'bottom-sheet', value: 'BottomSheet'},
    {id: 'account-banner', value: 'AccountBanner'},
    {id: 'leaderboard-header', value: 'LeaderboardHeader'},
]

const deviceOptions = [
    {id: 'pc', value: 'PC'},
    {id: 'mo', value: 'Mobile'},
]

export function PromotionSlotForm({promotionId, item, onSuccess, onCancel, onDelete}: IProps) {
    const {renderImage} = useImage()
    const [slot, setSlot] = useState<SlotType>(item?.slot || 'leaderboard-banner')
    const [device, setDevice] = useState<string>(item?.device || 'pc')
    const [url, setUrl] = useState<string>(item?.url || '')
    const [imageSource, setImageSource] = useState<string>(item?.image_source || '')
    const [background, setBackground] = useState<string>(item?.background || '')
    const [sizeCheckResult, setSizeCheckResult] = useState<SizeCheckResult | null>(null)
    const [isUploading, setIsUploading] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)

    // 권장 사이즈 가져오기
    const getExpectedSize = (slotType: string, deviceType: string) => {
        const config = SLOT_SIZE_CONFIG[slotType]
        return config?.[deviceType as 'pc' | 'mo']
    }

    // SLOT_SIZE_CONFIG 기반 aspect ratio 스타일 (인라인 스타일로 적용)
    const getAspectStyle = (slotType: string, deviceType: string): React.CSSProperties => {
        const size = getExpectedSize(slotType, deviceType)
        if (!size) return {aspectRatio: '16 / 9'} // 기본값
        return {aspectRatio: `${size.width} / ${size.height}`}
    }

    // 이미지 사이즈 체크 (권장 width로 리사이즈했을 때 height 비교)
    const checkImageSize = useCallback(
        (imageUrl: string, slotType: string, deviceType: string): Promise<SizeCheckResult | null> => {
            return new Promise(resolve => {
                const expected = getExpectedSize(slotType, deviceType)
                if (!expected) {
                    resolve(null)
                    return
                }

                const img = new Image()
                img.onload = () => {
                    const actual = {width: img.naturalWidth, height: img.naturalHeight}
                    const match = actual.height === expected.height
                    resolve({match, actual, expected})
                }
                img.onerror = () => resolve(null)
                // 권장 width로 리사이즈된 이미지 로드
                const separator = imageUrl.includes('?') ? '&' : '?'
                img.src = `${imageUrl}${separator}width=${expected.width}`
            })
        },
        [],
    )

    useEffect(() => {
        if (item) {
            setSlot(item.slot)
            setDevice(item.device)
            setUrl(item.url)
            setImageSource(item.image_source)
            setBackground(item.background || '')
        }
    }, [item])

    // slot 또는 device 변경 시, 이미지가 있으면 사이즈 다시 체크
    useEffect(() => {
        if (imageSource) {
            checkImageSize(imageSource, slot, device).then(result => {
                setSizeCheckResult(result)
            })
        } else {
            setSizeCheckResult(null)
        }
    }, [slot, device, imageSource, checkImageSize])

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setIsUploading(true)
            setSizeCheckResult(null)

            try {
                // Auto-detect slot type and device from filename
                const fileName = file.name.toLowerCase()

                let detectedSlot = slot
                let detectedDevice = device

                // 1. Detect Slot Type
                const matchedSlot = slotOptions.find(
                    opt => fileName.includes(opt.value.toLowerCase()) || fileName.includes(opt.id.toLowerCase()),
                )
                if (matchedSlot) {
                    detectedSlot = matchedSlot.id
                    setSlot(matchedSlot.id)
                }

                // 2. Detect Device
                if (fileName.includes('_pc') || fileName.includes(' pc') || fileName.includes('-pc')) {
                    detectedDevice = 'pc'
                    setDevice('pc')
                } else if (
                    fileName.includes('_mo') ||
                    fileName.includes(' mobile') ||
                    fileName.includes('-mobile') ||
                    fileName.includes('_mobile') ||
                    fileName.includes('mobileweb')
                ) {
                    detectedDevice = 'mo'
                    setDevice('mo')
                }

                const result = await postUploadImage(file, false, false)
                if (result?.image_url) {
                    setImageSource(result.image_url)

                    // 사이즈 체크 수행
                    const sizeResult = await checkImageSize(result.image_url, detectedSlot, detectedDevice)
                    setSizeCheckResult(sizeResult)
                }
            } finally {
                setIsUploading(false)
            }
        }
    }

    const handleSubmit = async () => {
        if (!imageSource) {
            alert('Please upload an image')
            return
        }

        const payload: Partial<IPromotionItem> = {
            promotion_set_id: Number(promotionId),
            slot,
            device,
            url,
            image_source: imageSource,
            background,
            banner_oid: item?.banner_oid || '', // Maintain legacy if needed, or send empty
        }

        let result
        if (item?.id) {
            result = await updatePromotionItem(promotionId, item.id, payload)
        } else {
            result = await createPromotionItem(promotionId, payload)
        }

        if (result?.data?.success || result?.data?.id) {
            alert('Slot saved successfully')
            onSuccess()
        } else {
            alert('Failed to save slot')
        }
    }

    return (
        <div className="p-1">
            <div className="flex justify-between items-center mb-6 border-b border-line02 pb-4">
                <Text className="text-h3 font-bold">{item ? 'Edit Slot' : 'New Slot'}</Text>
                <div className="flex gap-2">
                    {item && onDelete && <WizButton text="Delete Slot" type="red" onClick={onDelete} />}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-4">
                {/* Left Column: Settings (7 cols) */}
                <div className="col-span-7 space-y-4">
                    {/* Slot Type - Custom Grid */}
                    <div>
                        <Text className="text-body3 text-font02 mb-2">Slot Type</Text>
                        <div className="grid grid-cols-3 gap-2">
                            {slotOptions.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setSlot(opt.id)}
                                    className={`
                                        px-2 py-2 rounded-[5px] text-ti3 font-medium border transition-all text-center truncate
                                        ${
                                            slot === opt.id
                                                ? 'bg-sub_blue text-white border-sub_blue shadow-md'
                                                : 'bg-white text-font03 border-line02 hover:border-sub_blue/50 hover:bg-gray-50'
                                        }
                                    `}
                                    title={opt.value}>
                                    {opt.value}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Device Selection */}
                    <div>
                        <Text className="text-body3 text-font02 mb-2">Device</Text>
                        <WizSelectButton
                            data={deviceOptions}
                            activeValue={deviceOptions.find(o => o.id === device)}
                            onChange={v => setDevice(v.id)}
                            className="w-full"
                        />
                    </div>

                    {/* URL & Background */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <Text className="text-body3 text-font02 mb-1">URL</Text>
                            <UnderLineInput value={url} placeholder="https://..." onChanged={setUrl} />
                        </div>
                        <div className="col-span-2">
                            <Text className="text-body3 text-font02 mb-1">Background Color</Text>
                            <div className="flex gap-2 items-center">
                                <UnderLineInput
                                    value={background}
                                    placeholder="#FFFFFF"
                                    onChanged={setBackground}
                                    className="flex-1"
                                />
                                <div
                                    className="w-10 h-10 border border-line02 rounded shadow-sm"
                                    style={{backgroundColor: background}}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Image & Preview (5 cols) */}
                <div className="col-span-5 flex flex-col gap-4">
                    <Text className="text-body3 text-font02">Image & Preview</Text>

                    {/* Size Check Result / Guide Size Info - 이미지 프리뷰 위에 배치 */}
                    {isUploading ? (
                        <div className="p-3 rounded-lg border bg-gray-100 border-gray-200 animate-pulse">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gray-300 rounded-full" />
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-300 rounded w-24 mb-1" />
                                    <div className="h-3 bg-gray-200 rounded w-40" />
                                </div>
                            </div>
                        </div>
                    ) : sizeCheckResult ? (
                        <div
                            className={`p-3 rounded-lg border ${
                                sizeCheckResult.match ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                            }`}>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`text-lg ${sizeCheckResult.match ? 'text-green-600' : 'text-red-600'}`}>
                                    {sizeCheckResult.match ? '✓' : '✗'}
                                </span>
                                <div>
                                    <Text
                                        className={`text-ti2 font-bold ${
                                            sizeCheckResult.match ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                        {sizeCheckResult.match ? 'Size Match!' : 'Size Mismatch'}
                                    </Text>
                                    <Text className="text-ti3 text-font03">
                                        Actual: {sizeCheckResult.actual.width}x{sizeCheckResult.actual.height}
                                        {' / '}
                                        Guide: {sizeCheckResult.expected.width}x{sizeCheckResult.expected.height}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                            <Text className="text-ti3 text-font03">
                                Guide Size: {getExpectedSize(slot, device)?.width}x
                                {getExpectedSize(slot, device)?.height}
                            </Text>
                        </div>
                    )}

                    {/* Image Preview Area */}
                    <div
                        className={`w-full min-h-[280px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 hover:border-primary/50 transition-colors cursor-pointer group relative flex flex-col items-center justify-center p-4 overflow-hidden ${
                            isUploading ? 'pointer-events-none' : ''
                        }`}
                        onClick={() => !isUploading && fileInputRef.current?.click()}>
                        {/* Loading Skeleton */}
                        {isUploading ? (
                            <div className="w-full min-h-[200px] flex items-center justify-center relative p-4">
                                <div className="w-full h-[180px] bg-gray-200 rounded-[5px] animate-pulse flex items-center justify-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                        <Text className="text-ti3 text-gray-400">Uploading...</Text>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Fixed Height Preview Container */}
                                <div className="w-full flex items-center justify-center relative p-4 bg-gray-50/50">
                                    {/* Adaptive Preview Container - SLOT_SIZE_CONFIG 기반 동적 aspect ratio */}
                                    <div
                                        className="w-full relative flex items-center justify-center overflow-hidden rounded-[5px] border border-line02 shadow-sm bg-white"
                                        style={{
                                            backgroundColor: background || '#ffffff',
                                            ...getAspectStyle(slot, device),
                                        }}>
                                        {imageSource ? (
                                            <WizImage
                                                containerClassName="!flex-[auto]"
                                                src={`${imageSource}?type=webp&width=1000&quality=100`}
                                                className={'w-full h-auto '}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center text-gray-300 p-4 text-center">
                                                <Text className="text-ti1 opacity-50">
                                                    {slotOptions.find(o => o.id === slot)?.value}
                                                </Text>
                                            </div>
                                        )}

                                        {/* Visual Cues for specific types */}
                                        {slot.includes('bottom-sheet') && (
                                            <div className="absolute inset-0 border-b-[8px] border-gray-800/10 pointer-events-none" />
                                        )}
                                    </div>
                                </div>

                                {/* Hover Overlay */}
                                <div className="mt-3 text-center relative z-10 flex-shrink-0">
                                    <div className="bg-white border border-line02 px-3 py-1.5 rounded-full shadow-sm text-primary font-bold text-ti2 group-hover:bg-primary group-hover:text-white transition-colors">
                                        {imageSource ? 'Click to Change' : 'Upload Image'}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleImageUpload}
                        accept="image/*"
                    />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-line02">
                <WizButton text="Cancel" type="gray" onClick={onCancel} />
                <WizButton text={item ? 'Update Slot' : 'Create Slot'} type="primary" onClick={handleSubmit} />
            </div>
        </div>
    )
}
